import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

// MUI imports for side-by-side comparison
import {
  Button as MuiButton,
  Card as MuiCard,
  CardContent as MuiCardContent,
  TextField,
  Typography,
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions,
} from '@mui/material';

export function DesignSystemShowcase() {
  const [muiDialogOpen, setMuiDialogOpen] = useState(false);

  return (
    <div className="p-8 space-y-8 max-w-5xl mx-auto">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-foreground">
          Design System Showcase
        </h1>
        <p className="text-muted-foreground">
          shadcn/ui + MUI side by side — verifica coesistenza su branch design
        </p>
      </div>

      {/* Buttons */}
      <Card>
        <CardHeader>
          <CardTitle>Buttons</CardTitle>
          <CardDescription>shadcn/ui variants vs MUI variants</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-3">shadcn/ui</p>
              <div className="flex flex-wrap gap-3">
                <Button>Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="destructive">Destructive</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="link">Link</Button>
                <Button size="sm">Small</Button>
                <Button size="lg">Large</Button>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-3">MUI</p>
              <div className="flex flex-wrap gap-3">
                <MuiButton variant="contained">Contained</MuiButton>
                <MuiButton variant="contained" color="secondary">Secondary</MuiButton>
                <MuiButton variant="outlined">Outlined</MuiButton>
                <MuiButton variant="contained" color="error">Error</MuiButton>
                <MuiButton variant="text">Text</MuiButton>
                <MuiButton variant="contained" size="small">Small</MuiButton>
                <MuiButton variant="contained" size="large">Large</MuiButton>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards */}
      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>shadcn Card</CardTitle>
            <CardDescription>Con header, content e description</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Questo card usa Tailwind utilities per il layout.
              Border radius, shadow e padding dal design system shadcn/ui.
            </p>
          </CardContent>
        </Card>

        <MuiCard>
          <MuiCardContent>
            <Typography variant="h6" gutterBottom>MUI Card</Typography>
            <Typography variant="body2" color="text.secondary">
              Questo card usa MUI sx prop e Emotion CSS-in-JS.
              Border radius, shadow e padding dal tema MUI.
            </Typography>
          </MuiCardContent>
        </MuiCard>
      </div>

      {/* Inputs */}
      <Card>
        <CardHeader>
          <CardTitle>Inputs</CardTitle>
          <CardDescription>Form controls comparison</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">shadcn/ui</p>
              <Input placeholder="Email address..." />
              <Input placeholder="Disabled input..." disabled />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">MUI</p>
              <TextField size="small" placeholder="Email address..." fullWidth />
              <TextField size="small" placeholder="Disabled input..." disabled fullWidth />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <Card>
        <CardHeader>
          <CardTitle>Dialogs</CardTitle>
          <CardDescription>Modal comparison — both work independently</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Dialog>
              <DialogTrigger asChild>
                <Button>Open shadcn Dialog</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>shadcn Dialog</DialogTitle>
                  <DialogDescription>
                    Questo dialog usa Radix UI primitives con Tailwind styling.
                    Coesiste con MUI Dialog senza conflitti di z-index.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-4">
                  <Input placeholder="Campo nel dialog..." />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline">Annulla</Button>
                    <Button>Conferma</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <MuiButton variant="outlined" onClick={() => setMuiDialogOpen(true)}>
              Open MUI Dialog
            </MuiButton>
            <MuiDialog open={muiDialogOpen} onClose={() => setMuiDialogOpen(false)}>
              <MuiDialogTitle>MUI Dialog</MuiDialogTitle>
              <MuiDialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Questo dialog usa MUI + Emotion. Funziona indipendentemente da shadcn.
                </Typography>
                <TextField size="small" placeholder="Campo nel dialog..." fullWidth />
              </MuiDialogContent>
              <DialogActions>
                <MuiButton onClick={() => setMuiDialogOpen(false)}>Annulla</MuiButton>
                <MuiButton variant="contained" onClick={() => setMuiDialogOpen(false)}>Conferma</MuiButton>
              </DialogActions>
            </MuiDialog>
          </div>
        </CardContent>
      </Card>

      {/* Color palette */}
      <Card>
        <CardHeader>
          <CardTitle>TeamSystem Color Palette</CardTitle>
          <CardDescription>CSS variables mappati al brand TeamSystem</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-3">
            {[
              { name: 'Primary', bg: 'bg-primary', fg: 'text-primary-foreground', hex: '#0066CC' },
              { name: 'Secondary', bg: 'bg-secondary', fg: 'text-secondary-foreground', hex: '#1A3A5C' },
              { name: 'Destructive', bg: 'bg-destructive', fg: 'text-white', hex: '#ef4444' },
              { name: 'Muted', bg: 'bg-muted', fg: 'text-muted-foreground', hex: '#f3f4f6' },
              { name: 'Accent', bg: 'bg-accent', fg: 'text-accent-foreground', hex: '#f3f4f6' },
              { name: 'Card', bg: 'bg-card', fg: 'text-card-foreground', hex: '#ffffff' },
              { name: 'Background', bg: 'bg-background', fg: 'text-foreground', hex: '#f9fafb' },
              { name: 'Border', bg: 'bg-border', fg: 'text-foreground', hex: '#e5e7eb' },
            ].map(({ name, bg, fg, hex }) => (
              <div key={name} className={`${bg} ${fg} p-4 rounded-lg text-center`}>
                <p className="font-medium text-sm">{name}</p>
                <p className="text-xs opacity-75 mt-1">{hex}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
